import React, { useState, useEffect } from "react";
import { AlertCircle, Building2, ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { transfersApi } from "@/services/api/transfers";
import { toast } from "sonner";
import * as errorUtils from '@/utils/errors';

interface ProjectTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  componentCount?: number;
}

/**
 * Project Transfer Dialog
 *
 * Allows users to transfer a project from their personal ownership to an organization.
 * Requires approval from organization admin.
 */
export const ProjectTransferDialog: React.FC<ProjectTransferDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  projectName,
  componentCount = 0,
}) => {
  // Use explicit selector to ensure re-renders on organizations change
  const organizations = useOrganizationStore((state) => state.organizations);

  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingOrgs, setIsFetchingOrgs] = useState(false);

  // Fetch organizations when dialog opens
  useEffect(() => {
    if (open) {
      console.log("🔍 Transfer Dialog: Fetching organizations...");
      setIsFetchingOrgs(true);

      // Get fresh function from store to avoid stale closures
      const { fetchOrganizations } = useOrganizationStore.getState();

      fetchOrganizations()
        .then(() => {
          const currentOrgs = useOrganizationStore.getState().organizations;
          console.log("✅ Transfer Dialog: Organizations fetched successfully", {
            count: currentOrgs.length,
            organizations: currentOrgs.map(org => ({
              id: org._id,
              name: org.name,
              role: org.membership?.role
            }))
          });
          setIsFetchingOrgs(false);
        })
        .catch((error) => {
          console.error("❌ Transfer Dialog: Failed to fetch organizations:", error);
          toast.error("Failed to load organizations");
          setIsFetchingOrgs(false);
        });
    }
  }, [open]);


  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedOrgId("");
      setReason("");
      setNotes("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedOrgId) {
      toast.error("Please select an organization");
      return;
    }

    if (reason.trim().length < 10) {
      toast.error("Transfer reason must be at least 10 characters");
      return;
    }

    if (reason.trim().length > 500) {
      toast.error("Transfer reason cannot exceed 500 characters");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await transfersApi.initiateTransfer(projectId, {
        toOrganizationId: selectedOrgId,
        reason: reason.trim(),
        notes: notes.trim() || undefined,
      });

      // Check if transfer was immediately approved (user is org admin)
      if (result.status === "approved") {
        toast.success(
          `Transfer completed! "${projectName}" is now owned by ${selectedOrg?.name}.`
        );
      } else {
        toast.success(
          "Transfer request submitted successfully. Awaiting approval from organization admin."
        );
      }

      onOpenChange(false);
    } catch (error: unknown) {
      console.error("Failed to initiate transfer:", error);
      toast.error(
        errorUtils.getApiErrorMessage(error, "Failed to submit transfer request")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedOrg = organizations.find((org) => org._id === selectedOrgId);
  const isFormValid = selectedOrgId && reason.trim().length >= 10 && reason.trim().length <= 500;

  // Check if user is owner/admin of selected organization
  const isOrgAdmin = selectedOrg?.membership?.role === "owner" || selectedOrg?.membership?.role === "admin";
  const requiresApproval = !isOrgAdmin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-[500px] overflow-y-auto max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="text-card-foreground">Transfer project to organization</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Transfer ownership of "{projectName}" to an organization. This requires approval from an organization admin.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="px-6 pb-2 space-y-6">
          {/* Organization Selection */}
          <div className="space-y-2">
            <Label htmlFor="organization" className="text-sm font-medium text-card-foreground">
              Target organization <span className="text-destructive">*</span>
            </Label>
            <Select
              value={selectedOrgId}
              onValueChange={(value) => {
                console.log("🎯 Organization selected:", value);
                setSelectedOrgId(value);
              }}
              onOpenChange={(open) => {
                console.log("🎯 Dropdown opened:", open, "Organizations:", organizations);
              }}
            >
              <SelectTrigger id="organization" disabled={isFetchingOrgs}>
                <SelectValue placeholder={isFetchingOrgs ? "Loading organizations..." : "Select an organization"} />
              </SelectTrigger>
              <SelectContent className="z-[10000]">
                {(() => {
                  console.log("🎯 SelectContent RENDERING NOW:", {
                    isFetchingOrgs,
                    organizationsLength: organizations.length,
                    organizations: organizations.map(o => ({ id: o._id, name: o.name }))
                  });

                  if (isFetchingOrgs) {
                    return (
                      <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                        <Loader2 className="h-4 w-4 animate-spin mx-auto mb-1" />
                        Loading organizations...
                      </div>
                    );
                  }

                  if (organizations.length === 0) {
                    return (
                      <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                        No organizations available. You must be a member of an organization to transfer projects.
                      </div>
                    );
                  }

                  return (
                    <>
                      {organizations.map((org) => {
                        console.log("🎯 Rendering SelectItem for:", org.name, org._id);
                        return (
                          <SelectItem key={org._id} value={org._id}>
                            {org.name}
                            {org.membership && ` (${org.membership.role})`}
                          </SelectItem>
                        );
                      })}
                    </>
                  );
                })()}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              You must be a member of the target organization
            </p>
          </div>

          {/* Transfer Preview */}
          {selectedOrg && (
            <div className="rounded-md border border-border bg-muted/50 p-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="font-medium">Your Projects</div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <div className="flex items-center gap-1">
                    <Building2 className="h-4 w-4" />
                    <span className="font-medium">{selectedOrg.name}</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-muted-foreground">
                {componentCount > 0 && (
                  <div>{componentCount} component{componentCount !== 1 ? "s" : ""} will be transferred</div>
                )}
              </div>
            </div>
          )}

          {/* Transfer Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-sm font-medium text-card-foreground">
              Transfer reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why you're transferring this project (10-500 characters)"
              rows={4}
              maxLength={500}
              required
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Minimum 10 characters</span>
              <span>
                {reason.length}/500
              </span>
            </div>
          </div>

          {/* Optional Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-sm font-medium text-card-foreground">
              Additional notes <span className="text-muted-foreground font-normal">(Optional)</span>
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional information for the organization admin"
              rows={3}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground">
              {notes.length}/1000
            </p>
          </div>

          {/* Important Notice */}
          {selectedOrg && (
            <div className={`rounded-lg border p-4 ${
              isOrgAdmin
                ? "border-emerald-500/20 bg-emerald-500/10"
                : "border-amber-500/20 bg-amber-500/10"
            }`}>
              <div className="flex gap-3">
                <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                  isOrgAdmin ? "text-emerald-500" : "text-amber-500"
                }`} />
                <div className="space-y-1 text-sm">
                  <p className={`font-medium ${
                    isOrgAdmin ? "text-emerald-400" : "text-amber-400"
                  }`}>
                    {isOrgAdmin ? "Immediate transfer" : "Approval required"}
                  </p>
                  <ul className={`space-y-1 text-xs list-disc list-inside ${
                    isOrgAdmin ? "text-emerald-300/80" : "text-amber-300/80"
                  }`}>
                    {isOrgAdmin ? (
                      <>
                        <li>Transfer will execute immediately (you are {selectedOrg.membership?.role})</li>
                        <li>Project ownership will change to {selectedOrg.name}</li>
                        <li>You'll retain editor access to the project</li>
                      </>
                    ) : (
                      <>
                        <li>Transfer requires approval from an organization owner/admin</li>
                        <li>You'll retain editor access to the project after transfer</li>
                        <li>Request expires after 7 days if not approved</li>
                        <li>You can cancel the request before it's approved</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="-mx-6 mt-2 px-6 py-4 border-t border-border bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="border-border text-card-foreground hover:bg-muted"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isOrgAdmin ? "Transfer now" : "Submit request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
